import React from "react";
import {Section} from "../../../../../elements/section/Section";
import {ValidationResult} from "../../../../../elements/validator/component/ValidationComponent";
import {
    ConfigurationRegion,
    Em2DepartmentHashMappingDTO,
    Em2EnquiryTypeMappingDTOEventTypeEnum,
    Em2EnquiryVehicle,
    EnquiryProvider,
    EventType,
    LocationLeadConfigRequest,
    UpdateBotLeadSettingsRequest
} from "../../../../../../api/generated";
import {ValidatedSelectField} from "../../../../../elements/validator/component/ValidatedSelectField";
import {DefaultValidValidator, NotEmptyListValidator, NotEmptyStringValidator} from "../../../../../elements/validator/Validator";
import {SelectDropdown, SelectOption} from "../../../../../elements/select/SelectDropdown";
import {useAppDispatch, useAppSelector} from "../../../../../../app/hooks";
import {
    selectDepartmentHashMappings,
    selectEm2Region,
    selectEnquiryTypeMappings,
    selectLeadProvider,
    selectLocationDragon2000DealerId,
    selectLocationLeadConfigs,
    updateLeadSettingsRequest,
    updateLocationLeadConfig
} from "../leadsSlice";
import {
    SecondaryCollapsibleSection
} from "../../../../../elements/collapsableSection/secondary/SecondaryCollapsibleSection";
import {ValidatedInputField} from "../../../../../elements/validator/component/ValidatedInputField";
import {ValidatedMultiSelectField} from "../../../../../elements/validator/component/ValidatedMultiSelectField";
import {CustomSwitchField} from "../../../../../elements/customSwitchField/CustomSwitchField";
import styles from "./LeadIntegrationConfiguration.module.css";
import {configurationRegionOptions} from "../../../../location/types/configurationRegionOptions";
import {em2EnquiryTypeMappingEvents, em2EnquiryTypeOptions} from "../types/em2EnquiryTypes";
import {findEnquiryTypeMapping, setEnquiryTypeMapping} from "../util/em2EnquiryTypeMappingUtil";

interface LeadIntegrationConfigurationProps {
    updateValidationStatus: (status: ValidationResult<UpdateBotLeadSettingsRequest>) => void;
    getMessage: (f: string) => string;
}

const leadProviderOptions: SelectOption<EnquiryProvider>[] = [
    {label: "No Provider", value: EnquiryProvider.NoProvider},
    {label: "Dragon2000", value: EnquiryProvider.Dragon2000},
    {label: "EM2", value: EnquiryProvider.Em2}
];

const immediateMessageEventOptions: SelectOption<EventType>[] = [
    {label: "Requested Callback", value: EventType.RequestedCallback},
    {label: "Requested Test Drive", value: EventType.RequestedTestDrive},
    {label: "Requested Email Follow-up", value: EventType.RequestedEmailFollowUp},
    {label: "Requested Service Callback", value: EventType.RequestedServiceCallback},
    {label: "Booked Callback", value: EventType.BookedCallBack},
    {label: "Booked Email Follow-up", value: EventType.BookedEmailFollowUp},
    {label: "Booked Service Callback", value: EventType.BookedServiceCallBack},
    {label: "Booked Test Drive", value: EventType.BookedTestDrive},
    {label: "Returned Finance Application", value: EventType.ReturnedFinanceApplication},
    {label: "Returned Vehicle Valuation", value: EventType.ReturnedVehicleValuation}
];

export const LeadIntegrationConfiguration: React.FC<LeadIntegrationConfigurationProps> = ({
                                                                                              updateValidationStatus,
                                                                                              getMessage
                                                                                          }) => {
    const dispatch = useAppDispatch();
    const leadProvider = useAppSelector(selectLeadProvider);
    const locationLeadConfigs = useAppSelector(selectLocationLeadConfigs);
    const enquiryTypeMappings = useAppSelector(selectEnquiryTypeMappings);

    const showLocationTable = leadProvider && leadProvider !== EnquiryProvider.NoProvider;
    const getValidationFieldName = (locationPublicId: string, field: "dragon2000DealerId" | "em2Region") =>
        `locationLeadConfigs.${locationPublicId}.${field}`;
    const getDepartmentValidationFieldName = (locationPublicId: string, index: number, field: "departmentHash" | "eventTypes") =>
        `locationLeadConfigs.${locationPublicId}.departmentHashMappings.${index}.${field}`;

    const updateDepartmentMappings = (
        locationPublicId: string,
        mappings: Em2DepartmentHashMappingDTO[]
    ) => dispatch(updateLocationLeadConfig({
        locationPublicId,
        field: "departmentHashMappings",
        value: mappings
    }));

    const addDepartmentMapping = (config: LocationLeadConfigRequest) => {
        updateDepartmentMappings(config.locationPublicId || "", [
            ...(config.departmentHashMappings || []),
            {departmentHash: "", eventTypes: []}
        ]);
    };

    const updateDepartmentMapping = (
        config: LocationLeadConfigRequest,
        index: number,
        update: Partial<Em2DepartmentHashMappingDTO>
    ) => {
        const mappings = (config.departmentHashMappings || []).map((mapping: Em2DepartmentHashMappingDTO, mappingIndex: number) =>
            mappingIndex === index ? {...mapping, ...update} : mapping
        );
        updateDepartmentMappings(config.locationPublicId || "", mappings);
    };

    const removeDepartmentMapping = (config: LocationLeadConfigRequest, index: number) => {
        const mappings = (config.departmentHashMappings || []).filter((_: Em2DepartmentHashMappingDTO, mappingIndex: number) =>
            mappingIndex !== index
        );
        updateDepartmentMappings(config.locationPublicId || "", mappings);
    };

    const updateEnquiryTypeMapping = (
        eventType: Em2EnquiryTypeMappingDTOEventTypeEnum,
        vehicle: Em2EnquiryVehicle | undefined,
        enquiryTypeId: number | undefined
    ) => dispatch(updateLeadSettingsRequest({
        enquiryTypeMappings: setEnquiryTypeMapping(enquiryTypeMappings, eventType, vehicle, enquiryTypeId)
    }));

    return <Section title={"Lead Integration Configuration"}>
        <div className={styles.fieldsLine}>
            <ValidatedSelectField<EnquiryProvider, UpdateBotLeadSettingsRequest>
                required={false}
                onChange={(provider) => dispatch(updateLeadSettingsRequest({enquiryProvider: provider}))}
                updateValidationStatus={updateValidationStatus}
                fieldName={"enquiryProvider"}
                selector={selectLeadProvider}
                validator={DefaultValidValidator}
                label={"Lead Provider"}
                options={leadProviderOptions}
                getMessage={getMessage}
                selectFirstOptionByDefault={false}
            />
        </div>

        {showLocationTable && <SecondaryCollapsibleSection title={"Location Mapping"}>
            <div className={styles.table}>
                {locationLeadConfigs.map((config) => (
                    <div key={config.locationPublicId} className={styles.locationCard}>
                        <div className={styles.cardTopRow}>
                            <div className={styles.locationMeta}>
                                <span className={styles.locationCaption}>Location</span>
                                <span className={styles.locationName}>{config.locationName || "Unknown Location"}</span>
                            </div>
                            <div className={styles.summaryToggle}>
                                <span className={styles.summaryCaption}>Conversation Summary</span>
                                <CustomSwitchField
                                    textOnTrue={"On"}
                                    textOnFalse={"Off"}
                                    value={config.leadSummaryEnabled}
                                    toggleValue={(v) => dispatch(updateLocationLeadConfig({
                                        locationPublicId: config.locationPublicId || "",
                                        field: "leadSummaryEnabled",
                                        value: v
                                    }))}
                                />
                            </div>
                        </div>

                        <div className={styles.mappingFields}>
                            {leadProvider === EnquiryProvider.Dragon2000 &&
                                <ValidatedInputField<string, UpdateBotLeadSettingsRequest>
                                    required={false}
                                    onChange={(v) => dispatch(updateLocationLeadConfig({
                                        locationPublicId: config.locationPublicId || "",
                                        field: "dragon2000DealerId",
                                        value: v || ""
                                    }))}
                                    updateValidationStatus={updateValidationStatus}
                                    fieldName={getValidationFieldName(config.locationPublicId || "", "dragon2000DealerId")}
                                    selector={selectLocationDragon2000DealerId(config.locationPublicId || "")}
                                    validator={NotEmptyStringValidator}
                                    label={"Dealer ID"}
                                    placeholder={"Enter Dealer ID"}
                                    getMessage={getMessage}
                                />
                            }
                            {leadProvider === EnquiryProvider.Em2 &&
                                <div className={styles.em2Fields}>
                                    <ValidatedSelectField<ConfigurationRegion, UpdateBotLeadSettingsRequest>
                                        onChange={(v) => dispatch(updateLocationLeadConfig({
                                            locationPublicId: config.locationPublicId || "",
                                            field: "em2Region",
                                            value: v
                                        }))}
                                        updateValidationStatus={updateValidationStatus}
                                        fieldName={getValidationFieldName(config.locationPublicId || "", "em2Region")}
                                        selector={selectEm2Region(config.locationPublicId || "")}
                                        validator={DefaultValidValidator}
                                        label={"Region"}
                                        options={configurationRegionOptions}
                                        getMessage={getMessage}
                                    />
                                    <div className={styles.departmentMappings}>
                                        <div className={styles.departmentHeader}>
                                            <span className={styles.departmentTitle}>Department Hashes</span>
                                            <button
                                                type="button"
                                                className={styles.iconActionButton}
                                                onClick={() => addDepartmentMapping(config)}
                                                aria-label="Add department hash"
                                            >
                                                <img src="/plusIcon.svg" alt=""/>
                                            </button>
                                        </div>
                                        {(config.departmentHashMappings || []).map((mapping: Em2DepartmentHashMappingDTO, index: number) => (
                                            <div className={styles.departmentRow} key={`${config.locationPublicId}-department-${index}`}>
                                                <ValidatedInputField<string, UpdateBotLeadSettingsRequest>
                                                    onChange={(v) => updateDepartmentMapping(config, index, {departmentHash: v || ""})}
                                                    updateValidationStatus={updateValidationStatus}
                                                    fieldName={getDepartmentValidationFieldName(config.locationPublicId || "", index, "departmentHash")}
                                                    selector={(state) => selectDepartmentHashMappings(config.locationPublicId || "")(state)[index]?.departmentHash}
                                                    validator={NotEmptyStringValidator}
                                                    label={"Department Hash"}
                                                    placeholder={"Enter Department Hash"}
                                                    getMessage={getMessage}
                                                />
                                                <ValidatedMultiSelectField<EventType, UpdateBotLeadSettingsRequest>
                                                    onChange={(v) => updateDepartmentMapping(config, index, {eventTypes: v})}
                                                    updateValidationStatus={updateValidationStatus}
                                                    fieldName={getDepartmentValidationFieldName(config.locationPublicId || "", index, "eventTypes") as keyof UpdateBotLeadSettingsRequest}
                                                    selector={(state) => selectDepartmentHashMappings(config.locationPublicId || "")(state)[index]?.eventTypes}
                                                    validator={NotEmptyListValidator}
                                                    label={"Immediate Message Events"}
                                                    options={immediateMessageEventOptions}
                                                    getMessage={getMessage}
                                                    isEqual={(a, b) => a === b}
                                                />
                                                <button
                                                    type="button"
                                                    className={styles.iconActionButton}
                                                    onClick={() => removeDepartmentMapping(config, index)}
                                                    aria-label="Remove department hash"
                                                >
                                                    <img src="/deleteIcon.svg" alt=""/>
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            }
                        </div>
                    </div>
                ))}
            </div>
        </SecondaryCollapsibleSection>}

        {leadProvider === EnquiryProvider.Em2 &&
            <SecondaryCollapsibleSection title={"Enquiry Type Mapping"} collapsedByDefault>
                <div className={styles.enquiryTypeGrid}>
                    {em2EnquiryTypeMappingEvents.map(({eventType, vehicle, label, defaultEnquiryType}) => (
                        <SelectDropdown<number>
                            key={`enquiry-type-${eventType}-${vehicle || "any"}`}
                            title={label}
                            value={findEnquiryTypeMapping(enquiryTypeMappings, eventType, vehicle)?.enquiryTypeId}
                            handleChange={(v) => updateEnquiryTypeMapping(eventType, vehicle, v)}
                            options={em2EnquiryTypeOptions}
                            selectFirstOptionByDefault={false}
                            isClearable
                            placeholder={`Default: ${defaultEnquiryType}`}
                        />
                    ))}
                </div>
            </SecondaryCollapsibleSection>
        }
    </Section>
}
